import {
  Button,
  Drawer,
  Input,
  LegacyForm,
  QRCode,
  Space,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { Close, GoogleLogo, Key, Password } from "@payping/icons";
import React, { useEffect, useState } from "react";
import { accountApis } from "@payping/api";
import LittleActionDrawer from "@/components/mobile/LittleActionDrawer";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { DashboardRoutes } from "@/router/constant";

interface FormType {
  googleAuthCode: string;
}

const MobileEnableLogin2Fa: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = DashboardRoutes;
  const [qrCode, setQrCode] = useState<string | undefined>();

  const [showEnableQrDrawer, setShowEnableQrDrawer] = useState<boolean>(true);
  const [showEnableCodeDrawer, setShowEnableCodeDrawer] = useState<boolean>(false);

  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<FormType>();

  useEffect(() => {
    const getQrCodeUrl = async () => {
      const res = await accountApis.resetAuthenticatorKeyGet();
      setQrCode(res.barcodeUrl);
    };
    getQrCodeUrl();
  }, [qrCode]);

  const { retry: retryIsTwoFAActive } = useMessagedAsyncRetry(
    async () => {
      const state = await accountApis.getTwoFactorAuthenticationStatusPut();
      return state;
    },
    [],
    { useProgress: true },
  );

  const [{ loading: loadingEnable }, handleEnable] = useMessagedAsyncFn(
    async (values: FormType) => {
      await accountApis.enableAuthenticatorWithRequestModelPost({ code: values.googleAuthCode });
      retryIsTwoFAActive();
      form.resetFields();
      setShowEnableCodeDrawer(false);
    },
    [form, retryIsTwoFAActive],
  );

  return (
    <>
      {/*  <Show Qr Code Drawer> */}
      <Drawer
        title={t("user.2fa.modal.activate2fa")}
        open={showEnableQrDrawer}
        size={"100%"}
        placement={"bottom"}
        closeIcon={false}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              navigate(profile.m2fa.menu.url);
            }}
          />
        }
      >
        <Space orientation={"vertical"} className={"tw:w-full"} size={33}>
          <Space size={"large"}>
            <Password className={"color--gray500"} width={"64px"} />
            <GoogleLogo className={"color--gray500"} width={"64px"} />
          </Space>
          <Typography.Text type={"secondary"}>{t("user.2fa.modal.googleAuthDesc")}</Typography.Text>
          <QRCode size={200} status={qrCode === undefined ? "loading" : undefined} value={qrCode || ""} />
        </Space>
      </Drawer>

      <LittleActionDrawer
        open={showEnableQrDrawer}
        submit={
          <Button
            className={"tw:w-full"}
            loading={loadingEnable}
            type={"primary"}
            onClick={() => {
              setShowEnableQrDrawer(false);
              setShowEnableCodeDrawer(true);
            }}
          >
            {t("user.2fa.modal.nextStep")}
          </Button>
        }
        back={
          <Button
            className={"tw:w-full"}
            onClick={() => {
              setShowEnableQrDrawer(false);
              navigate(profile.m2fa.menu.url);
            }}
          >
            {t("user.2fa.modal.close")}
          </Button>
        }
      />

      {/*  </Show Qr Code Drawer> */}

      {/*  <Show Enable Code Drawer> */}
      <Drawer
        title={t("user.2fa.modal.activate2fa")}
        open={showEnableCodeDrawer}
        size={"100%"}
        placement={"bottom"}
        closeIcon={false}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              navigate(profile.m2fa.menu.url);
            }}
          />
        }
      >
        <Space orientation={"vertical"} className={"tw:w-full"} size={33}>
          <Space className={"tw:w-full"} size={"large"}>
            <Password className={"color--gray500"} width={"64px"} />
            <GoogleLogo className={"color--gray500"} width={"64px"} />
          </Space>
          <Space className={"tw:w-full"} orientation={"vertical"}>
            <Typography.Text type={"secondary"}>{t("user.2fa.modal.paypingCodeDesc")}</Typography.Text>
            <LegacyForm form={form} onFinish={handleEnable} layout={"vertical"}>
              <LegacyForm.Item label={t("user.2fa.modal.code")} name="googleAuthCode" rules={[{ required: true }]}>
                <Input.Password addonBefore={<Key width={"14px"} />} />
              </LegacyForm.Item>
            </LegacyForm>
          </Space>
        </Space>
      </Drawer>
      <LittleActionDrawer
        open={showEnableCodeDrawer}
        submit={
          <Button
            className={"tw:w-full"}
            loading={loadingEnable}
            type={"primary"}
            onClick={() => {
              form.submit();
              setShowEnableQrDrawer(false);
              setShowEnableCodeDrawer(true);
            }}
          >
            {t("common.activate")}
          </Button>
        }
        back={
          <Button
            className={"tw:w-full"}
            onClick={() => {
              setShowEnableCodeDrawer(false);
              navigate(profile.m2fa.menu.url);
            }}
          >
            {t("back")}
          </Button>
        }
      />
      {/*  </Show Enable Code Drawer> */}
    </>
  );
};

export default MobileEnableLogin2Fa;
